import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

interface GroupData {
  id: string;
  name: string;
}

interface MarkdownData {
  id: string;
  title: string;
  content: string;
  groupId: string;
}

const MarkdownEditor: React.FC = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groupList, setGroupList] = useState<GroupData[]>([]);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const savedGroups = localStorage.getItem('group-list');
    if (savedGroups) {
      setGroupList(JSON.parse(savedGroups));
    }

    if (id) {
      const saved = localStorage.getItem('markdown-list');
      if (saved) {
        const list: MarkdownData[] = JSON.parse(saved);
        const doc = list.find((d) => d.id === id);
        if (doc) {
          setTitle(doc.title);
          setContent(doc.content);
          setGroupId(doc.groupId);
        }
      }
    }
  }, [id]);

  const handleSave = () => {
    if (!title.trim()) {
      alert('タイトルを入力してください');
      return;
    }
    if (!groupId) {
      alert('グループを選択してください');
      return;
    }

    const saved = localStorage.getItem('markdown-list');
    const currentList: MarkdownData[] = saved ? JSON.parse(saved) : [];

    if (id) {
      const updatedList = currentList.map((doc) =>
        doc.id === id ? { ...doc, title, content, groupId } : doc
      );
      localStorage.setItem('markdown-list', JSON.stringify(updatedList));
    } else {
      const newDoc: MarkdownData = {
        id: uuidv4(),
        title,
        content,
        groupId,
      };
      const updatedList = [...currentList, newDoc];
      localStorage.setItem('markdown-list', JSON.stringify(updatedList));
    }

    alert('保存しました！');
    navigate('/admin/list');
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        {id ? 'Markdown編集' : '新規Markdown作成'}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
        gap={4}
        mt={2}
      >
        <Box>
          <Card>
            <CardContent>
              <TextField
                fullWidth
                label="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel id="group-select-label">グループ</InputLabel>
                <Select
                  labelId="group-select-label"
                  value={groupId}
                  label="グループ"
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  {groupList.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Markdown本文"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                rows={15}
                margin="normal"
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{ mt: 2 }}
              >
                保存
              </Button>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                プレビュー
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 2,
                  minHeight: 400,
                  overflowY: 'auto',
                  backgroundColor: '#fafafa',
                }}
              >
                {content ? (
                  <ReactMarkdown>{content}</ReactMarkdown>
                ) : (
                  <Typography color="text.secondary">
                    入力するとここにプレビューが表示されます。
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MarkdownEditor;
